/**
 * Notification Bell
 *
 * Bell icon with animated unread badge, placed in the Header.
 * Opens a PortalDropdown with NotificationPanel.
 * Manages SSE connection for real-time notification delivery.
 * Fires toast notifications for high-priority events.
 *
 * @module components/notifications/NotificationBell
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocaleNavigate } from '../../lib/i18n/exports';
import PortalDropdown from '@shared/ui/PortalDropdown';
import { usePortalDropdown } from '@shared/ui/PortalDropdown';
import { useToastActions } from '@shared/ui/toast/Toast';
import { useUnreadCount } from '../../entities/notification/hooks/useNotificationQueries';
import { useNotificationSSE } from '../../entities/notification/hooks/useNotificationSSE';
import NotificationPanel from './NotificationPanel';
import { resolveNotificationRoute } from '../../entities/notification/utils/resolveNotificationRoute';
import type { Notification } from '../../entities/notification/types';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Notification bell icon with badge and dropdown panel.
 * Self-contained: manages SSE connection, unread count, and toast display.
 */
const NotificationBell: React.FC = () => {
  const { t } = useTranslation('notifications');
  const navigate = useLocaleNavigate();
  const { info } = useToastActions();
  const hasConnectedRef = useRef(false);

  // Dropdown state
  const { isOpen, close, toggle, triggerRef, dropdownProps } =
    usePortalDropdown();

  // Unread count for badge
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;

  // ─── SSE Connection ────────────────────────────────────────────────────

  const handleNewNotification = useCallback(
    (notification: Notification) => {
      const isHighPriority =
        notification.priority === 'HIGH' || notification.priority === 'CRITICAL';

      if (isHighPriority) {
        const route = resolveNotificationRoute(notification, 'user');
        info(notification.title, notification.message, {
          duration: 8000,
          action: route
            ? {
                label: 'View',
                onClick: () => navigate(route),
              }
            : undefined,
        });
      }
    },
    [info, navigate]
  );

  const { connect } = useNotificationSSE({
    onNewNotification: handleNewNotification,
  });

  // Auto-connect SSE on mount (once)
  useEffect(() => {
    if (!hasConnectedRef.current) {
      hasConnectedRef.current = true;
      connect();
    }
  }, [connect]);

  // ─── Badge Display ─────────────────────────────────────────────────────

  const displayCount = unreadCount > 99 ? '99+' : String(unreadCount);
  const showBadge = unreadCount > 0;

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="relative">
      {/* Bell trigger button */}
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        onClick={toggle}
        className="relative flex h-10 w-10 items-center justify-center rounded-full text-gray-600 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
        aria-label={showBadge ? t('bell.labelWithCount', { count: unreadCount }) : t('bell.label')}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Bell className="h-5 w-5" />

        {/* Unread badge */}
        <AnimatePresence>
          {showBadge && (
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-linear-to-r from-emerald-500 to-teal-500 px-1 text-[10px] font-bold leading-none text-white shadow-xs"
            >
              {displayCount}
            </motion.span>
          )}
        </AnimatePresence>

      </button>

      {/* Dropdown panel */}
      <PortalDropdown
        {...dropdownProps}
        placement="bottom-end"
        minWidth={400}
        maxWidth={400}
        offset={8}
        className="rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
      >
        <NotificationPanel onClose={close} />
      </PortalDropdown>
    </div>
  );
};

export default NotificationBell;
